/**
 * Sync History Contracts
 *
 * Shared Zod schemas + inferred types for the `/imports/sync/history` endpoints
 * exposed by `SyncHistoryHandlers` (backend). Lives in `shared/` because both
 * sides consume the same shape:
 *  - Backend `routes.ts` binds these schemas to Fastify request validation
 *    (querystring, params, body) so every request is parsed through the
 *    canonical definition.
 *  - Frontend `import.service.ts` uses the inferred response types so UI code
 *    never drifts from what the API actually returns.
 *
 * Design notes:
 *  - `status` and `jobType` arrive as comma-separated strings on the wire
 *    (e.g. `?status=pending,processing`). The schema models them as
 *    `z.string()` here to stay compatible with the handler's existing
 *    `split(',')` parsing. Callers that want arrays can join before sending.
 *  - `page` / `limit` also arrive as strings (query string). We keep them
 *    as strings in the request schema to match handler behavior verbatim
 *    — the handler runs `parseInt()` internally — and return them as
 *    numbers in the response schema.
 *  - JSONB columns (`inputData`, `outputData`, `errorData`, `eventData`,
 *    `metadata`) are modeled as `z.unknown()` — they carry job-type-specific
 *    payloads that this contract deliberately does not pin down.
 */

import { z } from 'zod/v4'

// ============================================================
// ENUMS — mirror platform/database/schema/job.schema.ts
// ============================================================

/**
 * Job status values — mirror of `JOB_STATUSES` in
 * `packages/backend/src/platform/database/schema/job.schema.ts`.
 * Duplicated here (not imported) to preserve the shared package's
 * independence from backend sources.
 */
export const SYNC_JOB_STATUS_VALUES = [
  'pending',
  'processing',
  'paused',
  'completed',
  'failed',
  'cancelled',
] as const

export const SyncJobStatusSchema = z.enum(SYNC_JOB_STATUS_VALUES)
export type SyncJobStatus = z.infer<typeof SyncJobStatusSchema>

/**
 * Import job types persisted into `platform_jobs.job_type`.
 * The handler additionally normalizes these to `api | wallet | csv` via
 * `getJobTypeSyncType`.
 */
export const SYNC_JOB_TYPE_VALUES = ['api-import', 'wallet_import', 'csv-import'] as const
export const SyncJobTypeSchema = z.enum(SYNC_JOB_TYPE_VALUES)
export type SyncJobType = z.infer<typeof SyncJobTypeSchema>

/**
 * Normalized source type surfaced by the handler's
 * `getJobTypeSyncType` mapping (used in list responses).
 */
export const SYNC_SOURCE_TYPE_VALUES = ['api', 'wallet', 'csv'] as const
export const SyncSourceTypeSchema = z.enum(SYNC_SOURCE_TYPE_VALUES)
export type SyncSourceType = z.infer<typeof SyncSourceTypeSchema>

/**
 * Sortable columns accepted by the list endpoint. Kept narrow on purpose —
 * arbitrary strings open the door to SQL-injection-shaped bugs.
 */
export const SYNC_HISTORY_SORT_BY_VALUES = [
  'createdAt',
  'startedAt',
  'completedAt',
  'status',
  'progress',
] as const
export const SyncHistorySortBySchema = z.enum(SYNC_HISTORY_SORT_BY_VALUES)
export type SyncHistorySortBy = z.infer<typeof SyncHistorySortBySchema>

export const SyncHistorySortOrderSchema = z.enum(['asc', 'desc'])
export type SyncHistorySortOrder = z.infer<typeof SyncHistorySortOrderSchema>

// ============================================================
// REQUEST SCHEMAS
// ============================================================

/**
 * Query string for `GET /imports/sync/history`.
 *
 * Wire formats:
 *  - `page`, `limit`: numeric strings (handler parses via `parseInt`).
 *  - `status`, `jobType`: comma-separated strings (handler splits on `,`).
 *  - `sourceId`: UUID of an import source (handler filters in-memory).
 *  - `startDate`, `endDate`: ISO-8601 datetime strings.
 */
export const SyncHistoryQuerySchema = z.object({
  // Positive integers only (1+). Regex `^[1-9]\d*$` rejects "0", "-1",
  // "abc", "01" — preventing negative offsets reaching Postgres (which
  // errors on OFFSET -25) and `limit=0` blowing up `totalPages = Infinity`.
  // ⚠️ La forma NO basta: `9999999999999999999` encaja con el regex —son todo
  // dígitos y empieza por 1-9— y llega al controlador, que lo manda como texto
  // a un parámetro `bigint`. PostgreSQL responde «value out of range for type
  // bigint» y el cliente recibe un 500. MEDIDO el 2026-09-21 sobre
  // `/api/imports/sync/history?page=9999999999999999999`.
  //
  // El tope es el último entero exacto de JavaScript: por encima el valor ya
  // llega mal antes de que SQL lo vea. Es el MISMO control de magnitud que su
  // hermano `limit` ya tenía — aquí faltaba.
  page: z
    .string()
    .regex(/^[1-9]\d*$/, 'page must be a positive integer (>= 1)')
    .refine((v) => Number(v) <= Number.MAX_SAFE_INTEGER, 'page is out of range')
    .optional(),
  // Same positive-integer guard, plus a refine-based cap at 100 so a
  // malicious/curious client cannot ask for `?limit=10000` and pull an
  // unbounded slice. The handler additionally enforces the cap defensively.
  limit: z
    .string()
    .regex(/^[1-9]\d*$/, 'limit must be a positive integer (>= 1)')
    .refine((v) => parseInt(v, 10) <= 100, 'limit must be <= 100')
    .optional(),
  status: z.string().optional(),
  jobType: z.string().optional(),
  sourceId: z.string().uuid().optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  sortBy: SyncHistorySortBySchema.optional(),
  sortOrder: SyncHistorySortOrderSchema.optional(),
})
export type SyncHistoryQuery = z.infer<typeof SyncHistoryQuerySchema>

/**
 * URL params for `/imports/sync/history/:logId` and its `/retry` sibling.
 */
export const SyncHistoryLogIdParamsSchema = z.object({
  logId: z.string().uuid('logId must be a UUID'),
})
export type SyncHistoryLogIdParams = z.infer<typeof SyncHistoryLogIdParamsSchema>

/**
 * Body for `POST /imports/sync/history/retry-multiple`.
 * Caps the batch at 100 to keep the `Promise.allSettled` fan-out bounded.
 */
export const SyncHistoryRetryMultipleBodySchema = z.object({
  logIds: z
    .array(z.string().uuid('logId must be a UUID'))
    .min(1, 'At least one logId is required')
    .max(100, 'Cannot retry more than 100 jobs at once'),
})
export type SyncHistoryRetryMultipleBody = z.infer<typeof SyncHistoryRetryMultipleBodySchema>

// ============================================================
// RESPONSE SCHEMAS — list endpoint
// ============================================================

/**
 * Single row returned by `getSyncHistory`. Shape comes from
 * `enrichJobsWithSourceInfo` — it spreads the full `PlatformJob` row and
 * adds `sourceName`, `sourceType`, `sourceId`, `duration`.
 */
export const SyncHistoryJobSchema = z.object({
  // From platform_jobs row
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  createdBy: z.string().uuid(),
  jobType: z.string(),
  queueName: z.string(),
  priority: z.number().int(),
  status: SyncJobStatusSchema,
  progress: z.number().int(),
  inputData: z.unknown(),
  outputData: z.unknown().nullable(),
  errorData: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  maxRetries: z.number().int(),
  currentRetry: z.number().int(),
  retryDelay: z.number().int(),
  scheduledAt: z.string().datetime().nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  processingTimeMs: z.number().int().nullable(),
  workerInstanceId: z.string().nullable(),
  expiresAt: z.string().datetime().nullable(),

  // Enrichment added by the handler
  sourceName: z.string(),
  sourceType: z.string(),
  sourceId: z.string().uuid().nullable(),
  duration: z.number().int().nullable(),
  /**
   * Why retrying this job would be REFUSED, or null when it would be accepted.
   *
   * `'source_deleted'` — the job names a `bat.data_sources` row the tenant no
   * longer has, so `POST .../retry` answers `409 RETRY_SOURCE_DELETED` every
   * time. The server computes it from the same `decideRetryAgainstSource` rule
   * the endpoint enforces, so a client that hides the control on this flag can
   * never offer an action the endpoint would reject.
   *
   * Optional on the wire: a client reading a pre-#1076 response (or the admin
   * sync monitor, which builds its rows elsewhere) simply sees `undefined` and
   * behaves exactly as before.
   */
  retryBlockedReason: z.enum(['source_deleted']).nullable().optional(),
})
export type SyncHistoryJob = z.infer<typeof SyncHistoryJobSchema>

export const SyncHistoryPaginationSchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
})
export type SyncHistoryPagination = z.infer<typeof SyncHistoryPaginationSchema>

/**
 * Response payload for `GET /imports/sync/history`.
 * The wire envelope adds `{ success: true, data: ... }` — this schema
 * represents the `data` portion.
 */
export const SyncHistoryListResponseSchema = z.object({
  jobs: z.array(SyncHistoryJobSchema),
  pagination: SyncHistoryPaginationSchema,
})
export type SyncHistoryListResponse = z.infer<typeof SyncHistoryListResponseSchema>

// ============================================================
// RESPONSE SCHEMAS — detail endpoint
// ============================================================

/**
 * Entry in the detail response `timeline`. Mirrors `jobEvents` rows.
 */
export const SyncHistoryEventSchema = z.object({
  id: z.string().uuid(),
  eventType: z.string(),
  eventData: z.unknown().nullable(),
  message: z.string().nullable(),
  workerInstanceId: z.string().nullable(),
  createdAt: z.string().datetime(),
})
export type SyncHistoryEvent = z.infer<typeof SyncHistoryEventSchema>

/**
 * Core job block returned by the detail endpoint. Unlike the list row
 * this omits the enrichment fields (source info is broken out into a
 * sibling `dataSource` object).
 */
export const SyncHistoryDetailJobSchema = z.object({
  id: z.string().uuid(),
  jobType: z.string(),
  queueName: z.string(),
  priority: z.number().int(),
  status: SyncJobStatusSchema,
  progress: z.number().int(),
  inputData: z.unknown(),
  outputData: z.unknown().nullable(),
  errorData: z.unknown().nullable(),
  metadata: z.unknown().nullable(),
  createdAt: z.string().datetime(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  processingTimeMs: z.number().int().nullable(),
  currentRetry: z.number().int(),
  maxRetries: z.number().int(),
  workerInstanceId: z.string().nullable(),
})
export type SyncHistoryDetailJob = z.infer<typeof SyncHistoryDetailJobSchema>

/**
 * Data-source block attached to the detail response (nullable because the
 * source may have been deleted or the job may not carry a sourceId).
 */
export const SyncHistoryDataSourceSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    type: z.string(),
    exchange: z.string().nullable(),
    status: z.string(),
  })
  .nullable()
export type SyncHistoryDataSource = z.infer<typeof SyncHistoryDataSourceSchema>

/**
 * Aggregated statistics computed by `calculateJobStatistics` on the
 * detail endpoint.
 */
export const SyncHistoryStatisticsSchema = z.object({
  totalDuration: z.number().int().nullable(),
  eventCount: z.number().int().min(0),
  progressEvents: z.number().int().min(0),
  hasErrors: z.boolean(),
  retryCount: z.number().int().min(0),
})
export type SyncHistoryStatistics = z.infer<typeof SyncHistoryStatisticsSchema>

/**
 * Response payload (`data` block) for `GET /imports/sync/history/:logId`.
 */
export const SyncHistoryDetailSchema = z.object({
  job: SyncHistoryDetailJobSchema,
  dataSource: SyncHistoryDataSourceSchema,
  timeline: z.array(SyncHistoryEventSchema),
  statistics: SyncHistoryStatisticsSchema,
})
export type SyncHistoryDetail = z.infer<typeof SyncHistoryDetailSchema>

// ============================================================
// RESPONSE SCHEMAS — retry endpoints
// ============================================================

/**
 * Response shape for `POST /imports/sync/history/:logId/retry`.
 *
 * Two cases:
 *  - existing job re-queued → `jobId === logId`, no `originalJobId`.
 *  - retry cap hit, new job created → `jobId` differs, `originalJobId` set.
 */
export const SyncHistoryRetryResponseSchema = z.object({
  jobId: z.string().uuid(),
  status: z.literal('pending'),
  message: z.string(),
  originalJobId: z.string().uuid().optional(),
})
export type SyncHistoryRetryResponse = z.infer<typeof SyncHistoryRetryResponseSchema>

/**
 * Response shape for `POST /imports/sync/history/retry-multiple`.
 */
export const SyncHistoryRetryMultipleResponseSchema = z.object({
  succeeded: z.number().int().min(0),
  failed: z.number().int().min(0),
  total: z.number().int().min(0),
})
export type SyncHistoryRetryMultipleResponse = z.infer<
  typeof SyncHistoryRetryMultipleResponseSchema
>
